export default function MethodSection() {
  return (
    <section
      id="method"
      className="section-padding scroll-mt-24 bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage:
          "url('https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/dd09b505e9735ceb49b4ab0d41925161.png')",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-800 mb-4">
            Calm Body. Clear Mind.
          </h2>
          <p className="max-w-2xl mx-auto text-base leading-relaxed text-foreground-600">
            Your nervous system is constantly scanning the environment for signals of safety or danger. When it perceives threat, whether physical pain, emotional stress, or exhaustion, it responds by amplifying symptoms. The opposite is equally true.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* When Overwhelmed */}
          <div className="rounded-xl p-8 border border-background-200 bg-background-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-7 h-7 flex items-center justify-center text-primary-700">
                <i className="ri-alarm-warning-line text-lg" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-primary-800">When Overwhelmed</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 shrink-0" />
                Pain increases and spreads
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 shrink-0" />
                Fatigue deepens rapidly
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 shrink-0" />
                Brain fog intensifies
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 shrink-0" />
                Emotional regulation becomes harder
              </li>
            </ul>
          </div>

          {/* When Safe */}
          <div className="rounded-xl p-8 border border-[#6B8FA3]/30 bg-[#6B8FA3]/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-7 h-7 flex items-center justify-center text-[#6B8FA3]">
                <i className="ri-shield-check-line text-lg" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-primary-800">When Safe</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8FA3] mt-2 shrink-0" />
                The body begins to repair
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8FA3] mt-2 shrink-0" />
                The mind clears and focuses
              </li>
              <li className="flex items-start gap-3 text-sm text-foreground-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#6B8FA3] mt-2 shrink-0" />
                Resilience naturally rebuilds
              </li>
            </ul>
          </div>
        </div>

        {/* Micro-Tools */}
        <div className="mt-20 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-primary-800 mb-3">
            Energy-Safe Micro-Tools
          </h3>
          <p className="text-base mb-12 max-w-xl mx-auto leading-relaxed text-foreground-600">
            Simple and quick shifts to anchor your nervous system in real-time.
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Tool 1 */}
            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                <i className="ri-pause-circle-line text-xl text-primary-600" />
              </div>
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 bg-primary-100 text-primary-700">
                Crash Prevention
              </span>
              <h4 className="font-heading text-lg font-semibold text-primary-800 mb-2">
                The Preventative Micro-Break
              </h4>
              <p className="text-sm leading-relaxed text-foreground-600">
                The ultimate crash-prevention tool. Strategic, 5-minute pauses that interrupt the &ldquo;Boom and Bust&rdquo; cycle before it starts.
              </p>
            </div>

            {/* Tool 2 */}
            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#6B8FA3]/15 mx-auto mb-3">
                <i className="ri-eye-line text-xl text-[#6B8FA3]" />
              </div>
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 bg-[#6B8FA3]/15 text-[#6B8FA3]">
                Sensory Reset
              </span>
              <h4 className="font-heading text-lg font-semibold text-primary-800 mb-2">
                Environmental Orienting
              </h4>
              <p className="text-sm leading-relaxed text-foreground-600">
                A quick sensory reset. Gently anchor your nervous system by noticing three pleasant things in your current space.
              </p>
            </div>

            {/* Tool 3 */}
            <div className="text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#6B8FA3]/15 mx-auto mb-3">
                <i className="ri-shield-check-line text-xl text-[#6B8FA3]" />
              </div>
              <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 bg-[#6B8FA3]/15 text-[#6B8FA3]">
                Instant Calm
              </span>
              <h4 className="font-heading text-lg font-semibold text-primary-800 mb-2">
                Safe Place Anchoring
              </h4>
              <p className="text-sm leading-relaxed text-foreground-600">
                A mental shortcut to calm. Link a word, image or breath to a felt sense of safety you can access anywhere, instantly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}